import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Dumbbell, 
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  Send,
  User,
  ChevronRight,
  AlertCircle,
  Crown,
  Loader2,
  Trash2,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const CoachAssignmentsPage = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [hasPremium, setHasPremium] = useState(false);
  
  const [newWorkout, setNewWorkout] = useState({
    name: '',
    description: '',
    exercises: [],
    duration_minutes: 45,
    intensity: 'moderate',
    due_date: '',
    notes: ''
  });

  const [newExercise, setNewExercise] = useState({
    name: '',
    sets: 3,
    reps: '10',
    notes: ''
  });

  useEffect(() => {
    checkPremiumAccess();
    fetchAthletes();
    fetchAssignments();
  }, []);

  const checkPremiumAccess = () => {
    const premiumFeatures = user?.premium_features || [];
    setHasPremium(
      premiumFeatures.includes('workout_assignment') || 
      premiumFeatures.includes('premium') ||
      user?.role === 'org_admin'
    );
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/family/athletes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAthletes(response.data.athletes || []);
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/workouts/assigned/coach`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExercise = () => {
    if (!newExercise.name) {
      toast.error('Exercise name required');
      return;
    }
    setNewWorkout({
      ...newWorkout,
      exercises: [...newWorkout.exercises, { ...newExercise }]
    });
    setNewExercise({ name: '', sets: 3, reps: '10', notes: '' });
  };

  const handleRemoveExercise = (index) => {
    setNewWorkout({
      ...newWorkout,
      exercises: newWorkout.exercises.filter((_, i) => i !== index)
    });
  };

  const handleToggleAthlete = (athleteId) => {
    setSelectedAthletes(prev => 
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAthletes.length === athletes.length) {
      setSelectedAthletes([]);
    } else {
      setSelectedAthletes(athletes.map(a => a.user_id));
    }
  };

  const handleCreateAssignment = async () => {
    if (!newWorkout.name) {
      toast.error('Workout name required');
      return;
    }
    if (selectedAthletes.length === 0) {
      toast.error('Select at least one athlete');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/workouts/assign`, {
        workout: {
          name: newWorkout.name,
          description: newWorkout.description,
          exercises: newWorkout.exercises,
          duration_minutes: newWorkout.duration_minutes,
          intensity: newWorkout.intensity
        },
        assign_to: selectedAthletes,
        due_date: newWorkout.due_date || null,
        notes: newWorkout.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Workout assigned to ${response.data.assigned_count} athlete(s)!`);
      setShowCreateDialog(false);
      setNewWorkout({
        name: '',
        description: '',
        exercises: [],
        duration_minutes: 45,
        intensity: 'moderate',
        due_date: '',
        notes: ''
      });
      setSelectedAthletes([]);
      fetchAssignments();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('premium')) {
        toast.error('Premium feature required. Please upgrade to access.');
      } else {
        toast.error(detail || 'Failed to create assignment');
      }
    } finally {
      setCreating(false);
    }
  };

  // Premium gate
  if (!hasPremium) {
    return (
      <DashboardLayout title="Coach Assignments">
        <div className="px-4 py-6 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
            <CardContent className="p-8 text-center">
              <Crown className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-wider mb-2">
                Premium Feature
              </h2>
              <p className="text-aura-text-muted mb-6 max-w-md mx-auto">
                Workout Assignment is a premium feature for coaches and trainers. 
                Assign custom workouts to individuals or groups, track completion, and monitor progress.
              </p>
              <div className="space-y-4 text-left max-w-sm mx-auto mb-6">
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>Create custom workout templates</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>Assign to individuals or entire teams</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>Track completion in real-time</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <CheckCircle2 className="w-5 h-5 text-amber-400" />
                  <span>Set due dates and reminders</span>
                </div>
              </div>
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-heading uppercase tracking-wider">
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Coach Assignments">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider">
              Workout Assignments
            </h1>
            <p className="text-aura-text-muted mt-1">
              Create and assign workouts to your athletes
            </p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button 
                className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider"
                data-testid="create-assignment-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Assignment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-aura-surface border-white/10 max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Create Workout Assignment
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Workout Details */}
                <div className="space-y-4">
                  <h3 className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
                    Workout Details
                  </h3>
                  
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Workout Name *
                    </Label>
                    <Input
                      value={newWorkout.name}
                      onChange={(e) => setNewWorkout({ ...newWorkout, name: e.target.value })}
                      placeholder="e.g., Speed & Agility Session"
                      className="h-12 bg-aura-bg border-white/10 text-white"
                      data-testid="workout-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                        Duration (min)
                      </Label>
                      <Input
                        type="number"
                        value={newWorkout.duration_minutes}
                        onChange={(e) => setNewWorkout({ ...newWorkout, duration_minutes: parseInt(e.target.value) })}
                        className="h-12 bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                        Intensity
                      </Label>
                      <Select 
                        value={newWorkout.intensity} 
                        onValueChange={(v) => setNewWorkout({ ...newWorkout, intensity: v })}
                      >
                        <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          <SelectItem value="low" className="text-white">Low</SelectItem>
                          <SelectItem value="moderate" className="text-white">Moderate</SelectItem>
                          <SelectItem value="high" className="text-white">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Description
                    </Label>
                    <Textarea
                      value={newWorkout.description}
                      onChange={(e) => setNewWorkout({ ...newWorkout, description: e.target.value })}
                      placeholder="Brief description of the workout"
                      className="bg-aura-bg border-white/10 text-white min-h-[80px]"
                    />
                  </div>

                  {/* Exercises */}
                  <div className="space-y-3">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Exercises
                    </Label>
                    
                    {newWorkout.exercises.length > 0 && (
                      <div className="space-y-2">
                        {newWorkout.exercises.map((ex, i) => (
                          <div 
                            key={i} 
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                          >
                            <div>
                              <p className="text-white font-medium">{ex.name}</p>
                              <p className="text-sm text-aura-text-muted">
                                {ex.sets} sets × {ex.reps} reps
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemoveExercise(i)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        value={newExercise.name}
                        onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                        placeholder="Exercise name"
                        className="col-span-2 bg-aura-bg border-white/10 text-white"
                      />
                      <Input
                        type="number"
                        value={newExercise.sets}
                        onChange={(e) => setNewExercise({ ...newExercise, sets: parseInt(e.target.value) })}
                        placeholder="Sets"
                        className="bg-aura-bg border-white/10 text-white"
                      />
                      <Input
                        value={newExercise.reps}
                        onChange={(e) => setNewExercise({ ...newExercise, reps: e.target.value })}
                        placeholder="Reps"
                        className="bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddExercise}
                      className="w-full border-white/20 text-white hover:bg-white/10"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Exercise
                    </Button>
                  </div>
                </div>

                {/* Assignment Settings */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <h3 className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
                    Assignment Settings
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                        Due Date (Optional)
                      </Label>
                      <Input
                        type="date"
                        value={newWorkout.due_date}
                        onChange={(e) => setNewWorkout({ ...newWorkout, due_date: e.target.value })}
                        className="h-12 bg-aura-bg border-white/10 text-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">
                      Notes for Athletes
                    </Label>
                    <Textarea
                      value={newWorkout.notes}
                      onChange={(e) => setNewWorkout({ ...newWorkout, notes: e.target.value })}
                      placeholder="Any special instructions or motivation"
                      className="bg-aura-bg border-white/10 text-white min-h-[60px]"
                    />
                  </div>
                </div>

                {/* Select Athletes */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
                      Select Athletes ({selectedAthletes.length} selected)
                    </h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAll}
                      className="text-aura-primary hover:text-aura-primary-hover"
                    >
                      {selectedAthletes.length === athletes.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {athletes.length === 0 ? (
                    <div className="p-4 bg-white/5 rounded-lg text-center">
                      <AlertCircle className="w-8 h-8 text-aura-text-muted mx-auto mb-2" />
                      <p className="text-aura-text-muted text-sm">
                        No linked athletes found. Link athletes from the Supporter Dashboard.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                      {athletes.map((athlete) => (
                        <button
                          key={athlete.user_id}
                          type="button"
                          onClick={() => handleToggleAthlete(athlete.user_id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                            selectedAthletes.includes(athlete.user_id)
                              ? 'border-aura-primary bg-aura-primary/10'
                              : 'border-white/10 bg-white/5 hover:border-white/20'
                          }`}
                          data-testid={`select-athlete-${athlete.user_id}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            selectedAthletes.includes(athlete.user_id)
                              ? 'bg-aura-primary'
                              : 'bg-white/10'
                          }`}>
                            {selectedAthletes.includes(athlete.user_id) ? (
                              <CheckCircle2 className="w-4 h-4 text-white" />
                            ) : (
                              <User className="w-4 h-4 text-aura-text-muted" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{athlete.name}</p>
                            <p className="text-xs text-aura-text-muted">{athlete.sport || 'Athlete'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <Button
                  onClick={handleCreateAssignment}
                  disabled={creating || !newWorkout.name || selectedAthletes.length === 0}
                  className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12"
                  data-testid="submit-assignment-btn"
                >
                  {creating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Assign Workout
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-primary">
                {assignments.length}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Total Assigned</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-success">
                {assignments.reduce((sum, a) => sum + (a.completed_count || 0), 0)}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-aura-warning">
                {assignments.reduce((sum, a) => sum + (a.pending_count || 0), 0)}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-bold text-white">
                {athletes.length}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Athletes</p>
            </CardContent>
          </Card>
        </div>

        {/* Assignments List */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader>
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Your Assignments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-white/5 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-12">
                <Dumbbell className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
                <p className="text-aura-text-muted mb-4">No assignments yet</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                >
                  Create First Assignment
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment) => (
                  <div
                    key={assignment.assignment_id}
                    className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-white/20 transition-all"
                    data-testid={`assignment-${assignment.assignment_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-heading font-bold text-white">
                            {assignment.workout?.name || 'Workout'}
                          </h3>
                          <Badge className={`text-xs ${
                            assignment.completed_count === assignment.total_assigned
                              ? 'bg-aura-success/20 text-aura-success'
                              : 'bg-aura-warning/20 text-aura-warning'
                          }`}>
                            {assignment.completed_count}/{assignment.total_assigned} complete
                          </Badge>
                        </div>
                        <p className="text-sm text-aura-text-muted mb-2">
                          {assignment.workout?.description || 'No description'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-aura-text-muted">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {assignment.total_assigned} athletes
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {assignment.workout?.duration_minutes || 45} min
                          </span>
                          {assignment.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Due: {format(parseISO(assignment.due_date), 'MMM d')}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            Created: {format(parseISO(assignment.created_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-2xl font-heading font-bold text-white">
                            {Math.round((assignment.completed_count / assignment.total_assigned) * 100)}%
                          </div>
                          <div className="text-xs text-aura-text-muted">complete</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-aura-primary to-aura-secondary transition-all duration-500"
                        style={{ width: `${(assignment.completed_count / assignment.total_assigned) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default CoachAssignmentsPage;
